
import gsap from "gsap";
import { Group, AnimationAction, AnimationMixer, LoopOnce, PlaneGeometry, Mesh, MeshLambertMaterial, Color, AnimationUtils, MeshStandardMaterial, DataTexture } from "three";
import { config } from "./utils/config";
import { LoadedGLTFs } from "../../utils/types";
import { Video } from "./video";
import { awaitTimer } from "../../utils";
import { GLTF } from "three/examples/jsm/loaders/GLTFLoader";
import { Params } from "../../../utils/types";

export class Pages {
    public instance: Group;
    private pages: GLTF["scene"];
    private pen: GLTF["scene"];
    private whitePaperAction!: AnimationAction;
    private blackPaperAction!: AnimationAction;
    private penWhitePaperAction!: AnimationAction;
    private penBlackPaperAction!: AnimationAction;
    declare blackPage: Mesh;
    private urlParams;
    private params: keyof typeof config;
    private video: Video;
    private declare backPage: Mesh;
    private firstPaperPlayed: boolean;
    private declare pageDrawnOn: Mesh

    private declare mixer: AnimationMixer;
    private declare mixer2: AnimationMixer;
    private declare actions: { [name: string]: AnimationAction };

    constructor(gltfs: LoadedGLTFs, urlParams: Params) {
        this.instance = new Group;
        this.instance.visible = false;
        this.pages = gltfs.paper.scene;
        this.pen = gltfs.penAnim.scene;
        this.urlParams = urlParams;
        this.params = this.urlParams.penType + this.urlParams.penNum as keyof typeof config;
        this.firstPaperPlayed = false;

        this.video = new Video(urlParams);
        this.setUpPages();
        this.createAnimations(gltfs.paper, gltfs.penAnim);


        this.video.video.addEventListener("playing", (event) => {
            awaitTimer(140).then(() => {
                if (this.firstPaperPlayed == false) {
                    let delay = this.urlParams.penType == 'brush' ? 100 : 2500;
                    awaitTimer(delay).then(() => {
                        this.whitePaperAction.play();
                    });
                    this.penWhitePaperAction.play();

                }
                else {
                    this.video.setPaperBlack();
                    this.backPage.material = new MeshLambertMaterial({ color: 0x000000, transparent: true });
                    this.whitePaperAction.stop();
                    this.penWhitePaperAction.stop();
                    this.blackPaperAction.play();
                    this.penBlackPaperAction.play();
                    this.blackPage.visible = false;
                }
            })

        });
    }

    public setUpPages() {
        //Pages
        let paperGlbScale = config[this.params].paperGlbScale;
        let paperGlbPos = config[this.params].paperGlbPos;
        this.pages.scale.set(paperGlbScale[0], paperGlbScale[1], paperGlbScale[2])
        this.pages.position.set(paperGlbPos[0], paperGlbPos[1], paperGlbPos[2])
        this.pages.rotation.set(0, Math.PI, 0);
        this.instance.add(this.pages);
        this.instance.visible = false;
        this.pages.traverse((node: object) => {
            if (node instanceof Mesh) {
                if (node.name.includes('2')) {
                    this.pageDrawnOn = node;
                }
                else if (node.name.includes('4')) {
                    this.backPage = node;
                }
                node.material.transparent = true;
                node.material.opacity = 0;
            }
        })


        //PEN
        let penAnimScale = config[this.params].penAnimScale;
        this.pen.scale.set(penAnimScale, penAnimScale, penAnimScale)
        this.pen.position.set(0, 0, 0.18);
        this.pen.rotation.set(0, Math.PI, 0);
        this.instance.add(this.pen)


        this.pen.traverse((node: object) => {
            if (node instanceof Mesh) {
                if (node.name == "BRUSH_PEN_ONLY_Sharpie_Pens_Brush_2" || node.name == "BRUSH_PEN_ONLY_Sharpie_Pens_Brush_3" || node.name == "Pen_Bullet_2" || node.name == "Pen_Bullet_4") {
                    node.material = new MeshLambertMaterial({
                        color: new Color(config[this.params].penColour).convertSRGBToLinear()
                    });
                }
                if (node.name == "BRUSH_PEN_ONLY_Sharpie_Pens_Brush_4") {
                    node.material.color = new Color(1, 1, 1),
                        node.material.metalness = 1;

                }
                node.material.transparent = true;
                node.material.opacity = 0;


            }
        })

        let paperScale = config[this.params].paperScale;
        this.blackPage = new Mesh(new PlaneGeometry(paperScale[0], paperScale[1]), new MeshLambertMaterial({ color: new Color(0x000000) }));
        this.blackPage.position.z = 0.01;
        this.blackPage.visible = false;
        this.instance.add(this.blackPage)

        // let paperScale = config[this.params].paperScale;
        // let texture = new TextureLoader().load(placeholder);

        // this.blackPage = new Mesh(new PlaneGeometry(paperScale[0], paperScale[0] * 0.328125), new MeshLambertMaterial({ map: texture, transparent: true, opacity: 0.5 }));
        // this.blackPage.position.z = 0.1;
        // //this.blackPage.visible = false;
        // this.instance.add(this.blackPage)

    }

    private createAnimations = (paper: GLTF, pen: GLTF) => {
        this.mixer = new AnimationMixer(paper.scene);
        this.mixer.timeScale = 1;
        this.mixer2 = new AnimationMixer(pen.scene);
        this.mixer2.timeScale = 0.86;

        let paperAnims = paper.animations;
        this.whitePaperAction = this.mixer.clipAction(paperAnims[1]);
        this.whitePaperAction.clampWhenFinished = true;
        this.whitePaperAction.setLoop(LoopOnce, 1);
        this.blackPaperAction = this.mixer.clipAction(paperAnims[0]);
        this.blackPaperAction.clampWhenFinished = true;
        this.blackPaperAction.setLoop(LoopOnce, 1);
        let penAnims = pen.animations;
        this.penWhitePaperAction = this.mixer2.clipAction(penAnims[0]);
        this.penWhitePaperAction.clampWhenFinished = true;
        this.penWhitePaperAction.setLoop(LoopOnce, 1);
        this.penBlackPaperAction = this.mixer2.clipAction(penAnims[1]);
        this.penBlackPaperAction.clampWhenFinished = true;
        this.penBlackPaperAction.setLoop(LoopOnce, 1);
    };



    public playWhitePaperAnimation = () => {
        this.blackPage.visible = true;
        this.pageDrawnOn.material = this.video.material;
        this.video.playVideo();
        return new Promise<void>((resolve, _) => {

            // awaitTimer(155).then(() => {
            //     awaitTimer(1300).then(() => {
            //         this.whitePaperAction.play();
            //     })
            //     this.penWhitePaperAction.play();
            // })
            this.mixer?.addEventListener("finished", () => {
                console.log('white page turn finished')
                this.pages.visible = false;
            })
            this.mixer2?.addEventListener("finished", () => {
                console.log('pen anim finished')
                resolve();
            })
        })
    }

    public playBlackPaperAnimation = () => {
        console.log('play black paper animation')
        this.video.restartVideo()
        this.pages.visible = true;
        this.firstPaperPlayed = true;
        this.video.playVideo();

        return new Promise<void>((resolve, _) => {
            this.mixer?.addEventListener("finished", () => {
                console.log('black page turn finished')
                this.pages.visible = false;
            })
            this.mixer2?.addEventListener("finished", () => {
                console.log('pen anim finished')
                resolve();
            })
        })
    }

    public fadeInAnimation = () => {
        this.instance.visible = true;
        return new Promise<void>((resolve, _) => {
            this.instance.traverse((node: object) => {
                if (node instanceof Mesh) {
                    gsap.to(node.material, {
                        opacity: 1,
                        duration: 0.5,
                        ease: "sine.easeOut",
                        onComplete: resolve,
                    });
                }
            });
            this.pen.traverse((node: object) => {
                if (node instanceof Mesh) {
                    gsap.to(node.material, {
                        opacity: 1,
                        duration: 0.5,
                        ease: "sine.easeOut",
                        onUpdate: () => {
                            if (node.name == "BRUSH_PEN_ONLY_Sharpie_Pens_Brush_4") {
                                node.material.opacity = node.material.opacity / 2;
                            }
                        },
                    });
                }
            });

        })
    }

    public fadeOutAnimation = () => {
        return new Promise<void>((resolve, _) => {
            this.instance.traverse((node: object) => {
                if (node instanceof Mesh) {
                    gsap.to(node.material, {
                        opacity: 0,
                        duration: 0.5,
                        ease: "sine.easeOut",
                        onUpdate: () => {
                            if (node.name == "BRUSH_PEN_ONLY_Sharpie_Pens_Brush_4") {
                                node.material.opacity = node.material.opacity / 2;
                            }
                        },
                        onComplete: () => {
                            resolve()
                            this.instance.visible = false;
                        },
                    });
                }
            });
        })
    }

    update = (delta: number) => {
        if (this.mixer) this.mixer.update(delta);
        if (this.mixer2) this.mixer2.update(delta);
    };
}